import React, { useState, useEffect } from 'react';
import { loadingImage } from '../auth';
import VolumeForecast from './VolumeForecast';

const PPR_LINES = [
  // CPLH lines (rate = cartons / hours)
  { id: 'receiveDock', name: 'Receive Dock', type: 'cplh', cartonSource: 'ibCartons' },
  // UPH sub-items under Receive Dock
  { id: 'eachReceiveSmall', name: '  Each Receive - Small', type: 'uph', cartonSource: 'each' },
  { id: 'eachReceiveMedium', name: '  Each Receive - Medium', type: 'uph', cartonSource: 'each' },
  { id: 'eachReceiveLarge', name: '  Each Receive - Large', type: 'uph', cartonSource: 'each' },
  { id: 'eachReceiveHeavyBulky', name: '  Each Receive - Heavy/Bulky', type: 'uph', cartonSource: 'each' },
  { id: 'caseReceive', name: '  Case Receive', type: 'uph', cartonSource: 'caseVol' },
  { id: 'lpReceive', name: '  LP Receive', type: 'cplh', cartonSource: '_pidPlusAros' },
  { id: 'palletReceive', name: '  Pallet Receive', type: 'cplh', cartonSource: '_pallets' },
  { id: 'receiveSupport', name: 'Receive Support', type: 'cplh', cartonSource: 'ibCartons' },
  { id: 'caseTransferIn', name: '  Case Transfer In', type: 'uph', cartonSource: 'caseVol' },
  { id: 'transferInSupport', name: '  Transfer In Support', type: 'uph', cartonSource: 'ibCartons' },
  { id: 'cubiscan', name: '  Cubiscan', type: 'uph', cartonSource: 'each' },
  { id: 'prepRecorderSmall', name: '  Prep Recorder - Small', type: 'uph', cartonSource: 'prep' },
  { id: 'prepRecorderMedium', name: '  Prep Recorder - Medium', type: 'uph', cartonSource: 'prep' },
  { id: 'prepRecorderLarge', name: '  Prep Recorder - Large', type: 'uph', cartonSource: 'prep' },
  { id: 'prepRecorderHeavyBulky', name: '  Prep Recorder - Heavy/Bulky', type: 'uph', cartonSource: 'prep' },
  { id: 'prepSupport', name: '  Prep Support', type: 'uph', cartonSource: 'prep' },
  { id: 'rsrSupport', name: '  RSR Support', type: 'uph', cartonSource: 'ibCartons' },
  { id: 'ibLeadPa', name: '  IB Lead/PA', type: 'uph', cartonSource: 'ibCartons' },
  { id: 'ibProblemSolve', name: '  IB Problem Solve', type: 'uph', cartonSource: 'ibCartons' },
  // DA / Transfer Out
  { id: 'transferOut', name: 'Transfer Out', type: 'cplh', cartonSource: 'daCartons' },
  { id: 'transferOutDock', name: 'Transfer Out Dock', type: 'cplh', cartonSource: 'daCartons' },
  { id: 'toLeadPa', name: '  TO Lead/PA', type: 'uph', cartonSource: 'daCartons' },
  { id: 'toProblemSolve', name: '  TO Problem Solve', type: 'uph', cartonSource: 'daCartons' },
  { id: 'daBldgTotal', name: 'DA Bldg to Bldg Transfer TOTAL', type: 'cplh', cartonSource: 'daCartons' },
  // Support & Summary
  { id: 'adminHrIt', name: 'Admin/HR/IT', type: 'cplh', cartonSource: 'totalCartons' },
  { id: 'totalInbound', name: 'Total Inbound', type: 'cplh', cartonSource: 'ibCartons' },
  { id: 'transferOutSummary', name: 'Transfer-Out (FC Summary)', type: 'cplh', cartonSource: 'daCartons' },
  { id: 'support', name: 'Support', type: 'cplh', cartonSource: 'totalCartons' },
  { id: 'throughput', name: 'THROUGHPUT', type: 'cplh', cartonSource: 'totalCartons' },
  { id: '_prodThroughput', name: 'Production Only Throughput', type: 'calc' },
];

const METRICS_PER_LINE = ['rate', 'pctToPlan', 'hours', 'hoursSaved'];
const METRIC_LABELS = { rate: 'YTD Rate', pctToPlan: 'YTD % to Plan', hours: 'Hours', hoursSaved: 'YTD Hours Saved/Gained/Lost' };

function getHeatColor(pct) {
  if (!pct || pct === 0) return '#f5f5f5';
  if (pct >= 105) return '#c8e6c9';
  if (pct >= 100) return '#e8f5e9';
  if (pct >= 95) return '#fff9c4';
  if (pct >= 90) return '#ffcc80';
  return '#ef9a9a';
}

function getHoursColor(hours) {
  if (!hours) return '#f5f5f5';
  if (hours > 0) return '#c8e6c9';
  if (hours < 0) return '#ef9a9a';
  return '#f5f5f5';
}

// Common Sense Flat Staffing: these top-table lines and function-detail functions
// are locked to their 4-week average actual hours (flat across the whole forecast
// horizon) regardless of projected volume. Manual overrides still take precedence.
const FLAT_STAFF_LINES = new Set(['ibLeadPa', 'toLeadPa', 'adminHrIt']);
const FLAT_STAFF_FUNCTIONS = new Set(['Inbound Dock Clerk', 'Receive Dock TDR', 'MHE Support', 'Transfer Out Kaizen', 'Tote Replenishment', 'Ship Clerk']);

export default function Meesa({ api, site }) {
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(false);
  // Default to showing last 4 complete weeks + current partial week
  const getDefaultStart = () => {
    // Get current time in Pacific (UTC-7 in summer)
    const now = new Date();
    const pacificOffset = -7 * 60; // PDT
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    const pacNow = new Date(utcMs + pacificOffset * 60000);
    const day = pacNow.getDay(); // 0=Sun
    // Go back to this past Sunday
    const sunday = new Date(pacNow);
    sunday.setDate(pacNow.getDate() - day);
    // Go back 4 more weeks
    sunday.setDate(sunday.getDate() - 28);
    const y = sunday.getFullYear();
    const m = String(sunday.getMonth() + 1).padStart(2, '0');
    const d = String(sunday.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const [startWeek, setStartWeek] = useState(getDefaultStart());
  const [endWeek, setEndWeek] = useState('');
  const [essentialsOnly, setEssentialsOnly] = useState(true);
  // Ramp % inputs keep the raw typed text (so "0." -> "0.6" works) and are parsed where used.
  const [rampText, setRampText] = useState('0.5');
  const rampPct = (() => { const v = parseFloat(rampText); return Number.isFinite(v) ? v : 0; })();
  const DECIMAL_RE = /^\d*\.?\d*$/;
  const [planMode, setPlanMode] = useState('hwm'); // 'op' | 'best3of4' | 'hwm'
  const [forecastWeeks, setForecastWeeks] = useState(13);
  const [volForecast, setVolForecast] = useState({ totalCartons: 0, pidPct: 60, arosPct: 10, eolPct: 33 });
  const [volSaved, setVolSaved] = useState(false);
  const defaultWeek = { totalCartons: 500000, pidPct: 60, arosPct: 10, eolPct: 33, cpp: 28.2, eachPct: 0.01, casePct: 0.01, prepPct: 0.01 };
  const [weekInputs, setWeekInputs] = useState(Array(13).fill(null).map(() => ({ ...defaultWeek })));

  // Ensure weekInputs always matches forecastWeeks length
  const effectiveInputs = (() => {
    if (weekInputs.length >= forecastWeeks) return weekInputs.slice(0, forecastWeeks);
    const last = weekInputs[weekInputs.length - 1] || defaultWeek;
    return [...weekInputs, ...Array(forecastWeeks - weekInputs.length).fill(null).map(() => ({ ...last }))];
  })();
  const [funcRateOverrides, setFuncRateOverrides] = useState({}); // { "fnName": { 0: rate, 1: rate, ... } }
  const [funcHoursOverrides, setFuncHoursOverrides] = useState({}); // { "fnName": { 0: hours, 1: hours, ... } }
  const [funcRampOverrides, setFuncRampOverrides] = useState({}); // { "fnName": "0.25" } (raw text; older saves may be numbers)
  const rampFor = (fnName) => {
    const raw = funcRampOverrides[fnName];
    if (raw === undefined || raw === null || raw === '') return rampPct;
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : rampPct;
  };
  const [adminHoursOverrides, setAdminHoursOverrides] = useState({}); // { 0: hours, 1: hours, ... }
  const [flatStaffing, setFlatStaffing] = useState(true); // Common Sense Flat Staffing: lock listed lines/functions to 4wk avg hours
  const [overridesSaved, setOverridesSaved] = useState(false);
  const [toExpanded, setToExpanded] = useState(false); // Transfer Out top-line sub-bucket expander

  // Load saved overrides on mount
  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=MEESA_OVERRIDES&shift_type=overrides`)
      .then(r => r.json())
      .then(data => {
        if (data?.data) {
          if (data.data.funcRateOverrides) setFuncRateOverrides(data.data.funcRateOverrides);
          if (data.data.funcHoursOverrides) setFuncHoursOverrides(data.data.funcHoursOverrides);
          if (data.data.funcRampOverrides) setFuncRampOverrides(data.data.funcRampOverrides);
          if (data.data.adminHoursOverrides) setAdminHoursOverrides(data.data.adminHoursOverrides);
          if (data.data.flatStaffing !== undefined) setFlatStaffing(data.data.flatStaffing);
        }
      }).catch(() => {});
  }, [api, site]);

  const saveOverrides = async () => {
    await fetch(`${api}/api/fclm/cache`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: 'MEESA_OVERRIDES', shift_type: 'overrides', data: { funcRateOverrides, funcHoursOverrides, funcRampOverrides, adminHoursOverrides, flatStaffing } }),
    });
    setOverridesSaved(true);
    setTimeout(() => setOverridesSaved(false), 3000);
  };

  const resetOverrides = () => {
    setFuncRateOverrides({});
    setFuncHoursOverrides({});
    setFuncRampOverrides({});
    setAdminHoursOverrides({});
  };

  const hiddenLineIds = ['eachReceiveSmall', 'eachReceiveMedium', 'eachReceiveLarge', 'eachReceiveHeavyBulky', 'caseReceive', 'caseTransferIn', 'transferInSupport', 'cubiscan', 'prepRecorderSmall', 'prepRecorderMedium', 'prepRecorderLarge', 'prepRecorderHeavyBulky', 'prepSupport'];

  useEffect(() => {
    setLoading(true);
    fetch(`${api}/api/meesa?site=${site}`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setWeeklyData(data);
        else setWeeklyData([]);
        setLoading(false);
      })
      .catch(() => { setWeeklyData([]); setLoading(false); });
    // Load saved volume forecast
    fetch(`${api}/api/fclm/cache?site=${site}&date=VOLFORECAST&shift_type=inputs`)
      .then(r => r.json())
      .then(data => { if (data && !data.error && data.weeks) setWeekInputs(data.weeks); })
      .catch(() => {});
  }, [api, site]);

  // Filter by date range (hide weeks before 2025-12-28 except keep data stored)
  const thisSunday = (() => {
    const now = new Date();
    const pacNow = new Date(now.getTime() + now.getTimezoneOffset() * 60000 + (-7 * 60) * 60000);
    const s = new Date(pacNow); s.setDate(pacNow.getDate() - pacNow.getDay());
    return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(2, '0')}`;
  })();
  const filteredData = weeklyData.filter(w => {
    if (w.weekStart < '2025-12-28') return false; // Hide old weeks from display/calc
    if (w.weekStart >= thisSunday) return false; // Hide the current in-progress (partial) week
    if (startWeek && w.weekStart < startWeek) return false;
    if (endWeek && w.weekStart > endWeek) return false;
    return true;
  });

  // Compute YTD totals
  const ytdByLine = {};
  PPR_LINES.forEach(line => {
    let totalVolume = 0, totalHours = 0, totalPlanHours = 0, totalCartons = 0;
    filteredData.forEach(week => {
      const d = week.lines?.[line.id];
      if (d) {
        totalVolume += d.volume || 0;
        totalHours += d.hours || 0;
        totalPlanHours += d.planHours || 0;
      }
      // Sum cartons for CPLH lines
      if (line.type === 'cplh' && line.cartonSource) {
        if (line.cartonSource === '_pidPlusAros') {
          totalCartons += (week.cartons?.pid || 0) + (week.cartons?.aros || 0);
        } else if (line.cartonSource === '_pallets') {
          totalCartons += (week.cartons?.pr || 0) / 28.2;
        } else {
          totalCartons += week.cartons?.[line.cartonSource] || 0;
        }
      } else if (line.type === 'calc') {
        totalCartons += (week.cartons?.ibCartons || 0) + (week.cartons?.daCartons || 0);
      }
    });
    // For calc type, sum the specific hours
    if (line.type === 'calc') {
      totalHours = 0;
      filteredData.forEach(week => {
        totalHours += (week.lines?.totalInbound?.hours || 0) + (week.lines?.daBldgTotal?.hours || 0);
      });
    }
    // CPLH lines use cartons / hours, UPH lines use volume / hours
    const ytdRate = totalHours > 0 ? ((line.type === 'cplh' || line.type === 'calc') ? totalCartons / totalHours : totalVolume / totalHours) : 0;
    const ytdPlanRate = filteredData.length > 0 ? filteredData[filteredData.length - 1]?.lines?.[line.id]?.planRate || 0 : 0;
    const ytdPctToPlan = ytdPlanRate > 0 ? (ytdRate / ytdPlanRate) * 100 : 0;
    const ytdHoursSaved = totalPlanHours - totalHours;
    ytdByLine[line.id] = { rate: ytdRate, pctToPlan: ytdPctToPlan, hoursSaved: ytdHoursSaved, planRate: ytdPlanRate, hours: totalHours };
  });

  // Compute 13-week forecast
  // Compute getWeekCplh helper for forecast
  const getWeekCplhForLine = (week, line) => {
    if (line.type === 'calc') {
      const cartons = (week.cartons?.ibCartons || 0) + (week.cartons?.daCartons || 0);
      const hours = (week.lines?.totalInbound?.hours || 0) + (week.lines?.daBldgTotal?.hours || 0);
      return hours > 0 ? cartons / hours : 0;
    }
    if (line.type === 'cplh' && line.cartonSource) {
      let cartons = 0;
      if (line.cartonSource === '_pidPlusAros') {
        cartons = (week.cartons?.pid || 0) + (week.cartons?.aros || 0);
      } else if (line.cartonSource === '_pallets') {
        cartons = (week.cartons?.pr || 0) / 28.2;
      } else {
        cartons = week.cartons?.[line.cartonSource] || 0;
      }
      const hours = week.lines?.[line.id]?.hours || 0;
      return hours > 0 ? cartons / hours : 0;
    }
    return week.lines?.[line.id]?.rate || 0;
  };

  // Find HWM week (week with highest THROUGHPUT CPLH this year, excluding current week)
  const hwmWeek = (() => {
    let best = null, bestRate = 0;
    const weeksToCheck = weeklyData.filter(w => w.weekStart < thisSunday); // historical HWM across all weeks, not just selected timeframe
    weeksToCheck.forEach(w => {
      if (w.weekStart < '2026-01-01') return; // YTD only
      const cartons = w.cartons?.totalCartons || 0;
      const hours = w.lines?.throughput?.hours || 0;
      const rate = hours > 0 ? cartons / hours : 0;
      if (rate > bestRate) { bestRate = rate; best = w; }
    });
    return best;
  })();

  // Baseline: top 3 of 4 weeks prior to current week (last 4 complete weeks, take best 3)
  const forecastByLine = {};
  PPR_LINES.forEach(line => {
    const completeWeeks = filteredData.length > 1 ? filteredData.slice(-4) : [];
    const rates = completeWeeks.map(w => getWeekCplhForLine(w, line)).filter(r => r > 0);
    rates.sort((a, b) => b - a);
    const top3 = rates.slice(0, 3);
    const baseline = top3.length > 0 ? top3.reduce((s, v) => s + v, 0) / top3.length : 0;
    const forecast = [];
    for (let i = 1; i <= forecastWeeks; i++) {
      forecast.push(Math.round(baseline * (1 + (rampPct / 100) * i) * 10) / 10);
    }
    forecastByLine[line.id] = { baseline, forecast };
  });

  // Bottom-up override: for lines with function detail, derive forecast from function hours
  const BOTTOM_UP_LINES = ['receiveDock', 'receiveSupport', 'transferOut', 'transferOutDock'];
  const BU_LINE_CARTON = { receiveDock: 'ibCartons', receiveSupport: 'ibCartons', transferOut: 'daCartons', transferOutDock: 'daCartons' };

  // For each forecast week, compute bottom-up hours and derived rate.
  // IMPORTANT: this must use the SAME function list, carton map, rates, and
  // hours-override handling as the Function Detail tables below, so the
  // top-table line hours exactly equal the sum of the detail rows. We build a
  // shared `functionDetail` structure and the detail render consumes it too.
  const _fnCartonMapFor = (parentKey) => ({
    'Vendor Line Load': 'pid', 'PID Manual Divert': 'pid', 'ReceiveParcel Unload': 'pid', 'Receive Dock Crew': 'pid',
    'ReceiveLine Unloader': 'aros', 'PID Sort Line': 'aros',
    'MHE Support': parentKey === 'transferOut' ? 'aros' : 'pid', 'Robotic Problem Solv': 'aros', 'Jackpot Kickout': 'aros', 'Robotic Water Spider': 'aros',
    'Vendor Sort': 'pr', 'Offline_Mods': parentKey === 'receiveSupport' ? 'pr' : 'eol',
    'IB Robotics Operator': 'pr', 'Rcv WaterSpider': 'pr', 'Case Rcv WaterSpider': 'pr',
    'PID Truck Unload': 'pr',
    'Tote Replenishment': 'eol', 'Transfer Out Kaizen': 'eol', 'Water Spider': 'eol',
    'Transfer Out Packing': 'fluidOffline', 'Wall Builder': 'fluidDirect', 'Robotics Operator': 'fluidOffline',
    'Fluid Load - Case': 'fluidOffline',
    'TransferOut DockCrew': 'daCartons', 'Dock Pallet Loader': 'pr', 'IXD Pallet Loader': 'pr',
    'Receive Dock TDR': 'pid', 'Inbound Dock Clerk': 'pid', 'PID Box Opener': 'pid',
    // Support / lead / ambassador / training / audit / overflow functions have no
    // direct carton stream — scale them off the line's primary carton so they
    // forecast sensible (volume-scaled) hours instead of 0. Adjust if a function
    // should track a specific sub-stream (pid/aros/pr/eol) instead.
    // Receive lines -> ibCartons
    'FreightID': 'ibCartons', 'Pallet Unload': 'ibCartons', 'Problem Solver': 'ibCartons',
    'Receive Line Loader': 'ibCartons', 'Vendor Cutter': 'ibCartons',
    'PID Gatekeep': 'ibCartons', 'Receive Ambassador': 'ibCartons', 'Receive Training': 'ibCartons', 'General Audits': 'ibCartons',
    // Transfer-out lines -> daCartons
    'Palletize - Case': 'daCartons', 'Trans Out Overflow': 'daCartons', 'Transfer Out Ambssdr': 'daCartons',
    'TransferOut Training': 'daCartons', 'OB Ambassador': 'daCartons',
    'Outbound Dock Crew': 'daCartons', 'Pallet Movement': 'daCartons', 'Ship Clerk': 'daCartons', 'Truck Loader': 'daCartons',
  });
  const _volsForWeek = (w) => {
    const prPct = 100 - w.pidPct - w.arosPct;
    const pid = Math.round(w.totalCartons * w.pidPct / 100);
    const aros = Math.round(w.totalCartons * w.arosPct / 100);
    const pr = Math.round(w.totalCartons * prPct / 100);
    const eolV = Math.round(pid * w.eolPct / 100);
    return { ibCartons: w.totalCartons, obCartons: w.totalCartons, daCartons: w.totalCartons, totalCartons: w.totalCartons * 2, pid, aros, pr, eol: eolV, fluidDirect: pid - eolV, fluidOffline: eolV + aros, each: Math.round(w.totalCartons * (w.eachPct || 0.01) / 100), caseVol: Math.round(w.totalCartons * (w.casePct || 0.01) / 100), prep: Math.round(w.totalCartons * (w.prepPct || 0.01) / 100) };
  };
  const functionDetail = {};
  BOTTOM_UP_LINES.forEach(parentKey => {
    const cmap = _fnCartonMapFor(parentKey);
    // Unique function names present in the actual weekly data for this parent
    const functions = []; const _seen = new Set();
    filteredData.forEach(week => { (week.functions?.[parentKey] || []).forEach(fn => { if (!_seen.has(fn.name)) { _seen.add(fn.name); functions.push(fn.name); } }); });
    const getCalcRate = (week, fnName) => {
      const fn = (week.functions?.[parentKey] || []).find(f => f.name === fnName);
      if (!fn || !fn.hours) return 0;
      const ck = cmap[fnName];
      if (!ck || !week.cartons) return fn.rate || 0;
      const c = week.cartons[ck] || 0;
      return c > 0 ? c / fn.hours : 0;
    };
    const forecastRates = {}; const forecastHours = {};
    functions.forEach(fnName => {
      const completeWeeks = weeklyData.length > 1 ? filteredData.slice(-4) : [];
      const rs = completeWeeks.map(w => getCalcRate(w, fnName)).filter(r => r > 0);
      rs.sort((a, b) => b - a);
      const top3 = rs.slice(0, 3);
      const baseline = top3.length > 0 ? top3.reduce((s, v) => s + v, 0) / top3.length : 0;
      const fnRamp = rampFor(fnName);
      // 4-week average actual hours (used when this function is flat-staffed)
      const flatHrs = (() => {
        const hs = completeWeeks.map(w => (w.functions?.[parentKey] || []).find(f => f.name === fnName)?.hours || 0).filter(h => h > 0);
        return hs.length ? hs.reduce((s, v) => s + v, 0) / hs.length : 0;
      })();
      const isFlat = flatStaffing && FLAT_STAFF_FUNCTIONS.has(fnName);
      const fr = []; const fh = [];
      for (let i = 0; i < forecastWeeks; i++) {
        const rateOv = funcRateOverrides[fnName]?.[i];
        const hOv = funcHoursOverrides[fnName]?.[i];
        const vols = _volsForWeek(effectiveInputs[i]);
        const ck = cmap[fnName];
        const vol = ck ? (vols[ck] || 0) : 0;
        let rate, hrs;
        if (hOv !== undefined && hOv !== '') {
          // Manual hours override wins
          hrs = parseFloat(hOv);
          rate = (rateOv !== undefined && rateOv !== null && rateOv !== '') ? parseFloat(rateOv) : (hrs > 0 ? vol / hrs : 0);
        } else if (rateOv !== undefined && rateOv !== null && rateOv !== '') {
          // Manual rate override next
          rate = parseFloat(rateOv);
          hrs = rate > 0 ? vol / rate : 0;
        } else if (isFlat) {
          // Flat staffing: lock hours to the 4-week average, rate floats with volume
          hrs = flatHrs;
          rate = hrs > 0 ? vol / hrs : 0;
        } else {
          // Normal volume-scaled forecast (baseline + ramp)
          rate = Math.round(baseline * (1 + (fnRamp / 100) * (i + 1)) * 10) / 10;
          hrs = rate > 0 ? vol / rate : 0;
        }
        fr.push(rate); fh.push(hrs);
      }
      forecastRates[fnName] = fr; forecastHours[fnName] = fh;
    });
    functionDetail[parentKey] = { functions, forecastRates, forecastHours, getCalcRate, cmap };
  });

  // Bottom-up line forecast hours = SUM of its function-detail hours, so the
  // top table exactly matches the sum of the Function Detail table below.
  BOTTOM_UP_LINES.forEach(lineId => {
    const fd = functionDetail[lineId];
    if (!fd) return;
    const lineCartonKey = BU_LINE_CARTON[lineId];
    const buForecast = [];
    const buHours = [];
    for (let i = 0; i < forecastWeeks; i++) {
      let totalFnHours = 0;
      fd.functions.forEach(fn => { totalFnHours += fd.forecastHours[fn]?.[i] || 0; });
      buHours.push(totalFnHours);
      const lineVol = _volsForWeek(effectiveInputs[i])[lineCartonKey] || 0;
      buForecast.push(totalFnHours > 0 ? Math.round(lineVol / totalFnHours * 10) / 10 : 0);
    }
    forecastByLine[lineId] = { ...forecastByLine[lineId], forecast: buForecast, buHours };
  });

  // 4-week average actual hours for a top-table line (used for flat staffing)
  const avg4wkLineHours = (lineId) => {
    const cw = filteredData.length > 1 ? filteredData.slice(-4) : [];
    const hs = cw.map(w => w.lines?.[lineId]?.hours || 0).filter(h => h > 0);
    return hs.length ? hs.reduce((s, v) => s + v, 0) / hs.length : 0;
  };
  // Flat-staff standalone top-table lines (IB Lead/PA, TO Lead/PA): lock hours to
  // their 4-week average regardless of volume (rate floats). Runs before rollups
  // so Total Inbound / Transfer-Out totals include the flat hours.
  if (flatStaffing) {
    ['ibLeadPa', 'toLeadPa'].forEach(lineId => {
      if (!FLAT_STAFF_LINES.has(lineId)) return;
      const line = PPR_LINES.find(l => l.id === lineId);
      const flatAvg = avg4wkLineHours(lineId);
      const buHours = []; const buForecast = [];
      for (let i = 0; i < forecastWeeks; i++) {
        const vol = _volsForWeek(effectiveInputs[i])[line?.cartonSource || 'totalCartons'] || 0;
        buHours.push(flatAvg);
        buForecast.push(flatAvg > 0 ? Math.round(vol / flatAvg * 10) / 10 : 0);
      }
      forecastByLine[lineId] = { ...forecastByLine[lineId], buHours, forecast: buForecast };
    });
  }

  // Rollup lines: hours are sums of component line hours
  const TOTAL_IB_COMPONENTS = ['receiveDock', 'eachReceiveSmall', 'eachReceiveMedium', 'eachReceiveLarge', 'eachReceiveHeavyBulky', 'caseReceive', 'lpReceive', 'palletReceive', 'receiveSupport', 'caseTransferIn', 'transferInSupport', 'cubiscan', 'prepRecorderSmall', 'prepRecorderMedium', 'prepRecorderLarge', 'prepRecorderHeavyBulky', 'prepSupport', 'rsrSupport', 'ibLeadPa', 'ibProblemSolve'];
  const TO_SUMMARY_COMPONENTS = ['transferOut', 'transferOutDock', 'toLeadPa', 'toProblemSolve'];

  // Compute rollup buHours for each forecast week
  const getRollupHours = (components, weekIdx) => {
    return components.reduce((sum, lineId) => {
      if (forecastByLine[lineId]?.buHours) return sum + (forecastByLine[lineId].buHours[weekIdx] || 0);
      // For non-bottom-up lines, calc hours from vol/rate
      // effectiveInputs is padded to forecastWeeks (weekInputs holds only the first 13).
      const w = effectiveInputs[weekIdx];
      if (!w) return sum;
      const prPct = 100 - w.pidPct - w.arosPct;
      const pid = Math.round(w.totalCartons * w.pidPct / 100);
      const aros = Math.round(w.totalCartons * w.arosPct / 100);
      const pr = Math.round(w.totalCartons * prPct / 100);
      const eol = Math.round(pid * w.eolPct / 100);
      const vols = { ibCartons: w.totalCartons, obCartons: w.totalCartons, daCartons: w.totalCartons, totalCartons: w.totalCartons * 2, pid, aros, pr, eol, fluidDirect: pid - eol, fluidOffline: eol + aros, _pidPlusAros: pid + aros, _pallets: pr / (w.cpp || 28.2), each: Math.round(w.totalCartons * (w.eachPct || 0.01) / 100), caseVol: Math.round(w.totalCartons * (w.casePct || 0.01) / 100), prep: Math.round(w.totalCartons * (w.prepPct || 0.01) / 100) };
      const line = PPR_LINES.find(l => l.id === lineId);
      if (!line) return sum;
      const volKey = line.cartonSource || 'totalCartons';
      const vol = vols[volKey] || 0;
      const rate = forecastByLine[lineId]?.forecast?.[weekIdx] || 0;
      return sum + (rate > 0 ? vol / rate : 0);
    }, 0);
  };

  // Total Inbound
  const ibBuHours = [...Array(forecastWeeks)].map((_, i) => getRollupHours(TOTAL_IB_COMPONENTS, i));
  forecastByLine['totalInbound'] = { ...forecastByLine['totalInbound'], buHours: ibBuHours, forecast: ibBuHours.map((h, i) => { const w = effectiveInputs[i]; return h > 0 ? w.totalCartons / h : 0; }) };

  // Transfer Out Summary + DA Bldg Total (same hours)
  const toBuHours = [...Array(forecastWeeks)].map((_, i) => getRollupHours(TO_SUMMARY_COMPONENTS, i));
  forecastByLine['transferOutSummary'] = { ...forecastByLine['transferOutSummary'], buHours: toBuHours, forecast: toBuHours.map((h, i) => { const w = effectiveInputs[i]; return h > 0 ? w.totalCartons / h : 0; }) };
  forecastByLine['daBldgTotal'] = { ...forecastByLine['daBldgTotal'], buHours: toBuHours, forecast: toBuHours.map((h, i) => { const w = effectiveInputs[i]; return h > 0 ? w.totalCartons / h : 0; }) };

  // Production Only Throughput = Total Inbound + Transfer Out Summary
  const prodBuHours = ibBuHours.map((h, i) => h + toBuHours[i]);
  forecastByLine['_prodThroughput'] = { ...forecastByLine['_prodThroughput'], buHours: prodBuHours, forecast: prodBuHours.map((h, i) => { const w = effectiveInputs[i]; return h > 0 ? (w.totalCartons * 2) / h : 0; }) };

  // Admin/HR/IT hours (manual override > flat 4wk avg > top-down)
  const adminFlatAvg = avg4wkLineHours('adminHrIt');
  const adminIsFlat = flatStaffing && FLAT_STAFF_LINES.has('adminHrIt');
  const adminBuHours = [...Array(forecastWeeks)].map((_, i) => {
    if (adminHoursOverrides[i] !== undefined && adminHoursOverrides[i] !== '') return parseFloat(adminHoursOverrides[i]) || 0;
    if (adminIsFlat) return adminFlatAvg;
    const w = effectiveInputs[i];
    const rate = forecastByLine['adminHrIt']?.forecast?.[i] || 0;
    return rate > 0 ? (w.totalCartons * 2) / rate : 0;
  });
  forecastByLine['adminHrIt'] = { ...forecastByLine['adminHrIt'], buHours: adminBuHours,
    forecast: adminIsFlat
      ? adminBuHours.map((h, i) => { const w = effectiveInputs[i]; return h > 0 ? Math.round((w.totalCartons * 2) / h * 10) / 10 : 0; })
      : forecastByLine['adminHrIt']?.forecast };

  // Support hours = Admin/HR/IT + hidden buckets
  // Hidden buckets = baseline Support hours - baseline Admin hours (stays constant)
  const supportBuHours = [...Array(forecastWeeks)].map((_, i) => {
    const w = effectiveInputs[i];
    // Baseline support hours (from top-down rate before any override)
    const supportRate = forecastByLine['support']?.forecast?.[i] || 0;
    const baselineSupportHours = supportRate > 0 ? (w.totalCartons * 2) / supportRate : 0;
    // Baseline admin hours (what admin would be without override)
    const adminRate = forecastByLine['adminHrIt']?.forecast?.[i] || 0;
    const baselineAdminHours = adminRate > 0 ? (w.totalCartons * 2) / adminRate : 0;
    // Hidden = support - admin at baseline
    const hiddenHours = baselineSupportHours - baselineAdminHours;
    // Actual admin hours (with override)
    const actualAdminHours = adminBuHours[i];
    return actualAdminHours + Math.max(hiddenHours, 0);
  });
  forecastByLine['support'] = { ...forecastByLine['support'], buHours: supportBuHours };

  // Throughput = Prod Throughput + Support
  const thruBuHours = prodBuHours.map((h, i) => h + supportBuHours[i]);
  forecastByLine['throughput'] = { ...forecastByLine['throughput'], buHours: thruBuHours, forecast: thruBuHours.map((h, i) => { const w = effectiveInputs[i]; return h > 0 ? (w.totalCartons * 2) / h : 0; }) };

  // Get plan target for a line based on planMode
  const getPlanTarget = (week, line) => {
    if (planMode === 'op') return week.lines?.[line.id]?.planRate || 0;
    if (planMode === 'best3of4') return forecastByLine[line.id]?.forecast[0] || 0; // F1 rate
    if (planMode === 'hwm') return hwmWeek ? getWeekCplhForLine(hwmWeek, line) : 0;
    return 0;
  };

  const cellStyle = { padding: '3px 6px', textAlign: 'center', fontSize: '0.7rem', fontWeight: 500, border: '1px solid #000', whiteSpace: 'nowrap' };
  const headerStyle = { ...cellStyle, fontWeight: 700, background: '#232f3e', color: '#fff' };
  const labelStyle = { ...cellStyle, textAlign: 'left', fontWeight: 600, background: '#fff', position: 'sticky', left: 0, zIndex: 1 };
  const subLabelStyle = { ...cellStyle, textAlign: 'left', fontSize: '0.65rem', color: '#555', paddingLeft: 24 };

  return (
    <div>
      <h2 className="section-title">Bottoms Up Forecast — {site}</h2>
      <div className="card">
        <div className="form-row">
          <div className="form-group">
            <label>Start Week</label>
            <input type="date" value={startWeek} onChange={e => setStartWeek(e.target.value)} />
          </div>
          <div className="form-group">
            <label>End Week</label>
            <input type="date" value={endWeek} onChange={e => setEndWeek(e.target.value)} />
          </div>
          <div className="form-group">
            <button className={`btn ${essentialsOnly ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setEssentialsOnly(!essentialsOnly)} style={{ fontSize: '0.8rem', padding: '6px 14px' }}>
              {essentialsOnly ? '📋 Show All' : '🫙 Just the essentials okeeday!'}
            </button>
          </div>
          <div className="form-group">
            <button className={`btn ${flatStaffing ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFlatStaffing(!flatStaffing)} style={{ fontSize: '0.8rem', padding: '6px 14px' }} title="Lock IB Lead/PA, TO Lead/PA, Admin/HR/IT + select functions (IB Clerk, IB TDR, Jam Clear, EOL Divert, EOL Avery, Ship Clerk) to their 4-week average hours regardless of volume">
              {flatStaffing ? '🔒 Flat Staffing: ON' : '🔓 Flat Staffing: OFF'}
            </button>
          </div>
          <div className="form-group">
            <label>WoW Ramp %</label>
            <input type="text" inputMode="decimal" aria-label="Week over week ramp percent" value={rampText} onChange={e => { if (DECIMAL_RE.test(e.target.value)) setRampText(e.target.value); }} style={{ width: 60, textAlign: 'center' }} />
          </div>
          <div className="form-group">
            <button className={`btn ${forecastWeeks === 52 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setForecastWeeks(forecastWeeks === 13 ? 52 : 13)} style={{ fontSize: '0.75rem', padding: '5px 12px' }}>
              {forecastWeeks === 52 ? '📅 52 Weeks' : '📅 13 Weeks'}
            </button>
          </div>
          <div className="form-group">
            <label>% to Plan Mode</label>
            <select value={planMode} onChange={e => setPlanMode(e.target.value)}>
              <option value="op">% to OP Plan</option>
              <option value="best3of4">% to Best 3 of 4</option>
              <option value="hwm">% to HWM</option>
            </select>
          </div>
          <div className="form-group" style={{ justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={async () => {
              if (!window.confirm('Sync Bottoms Up Forecast data from Andes? This may take 30-60 seconds.')) return;
              setLoading(true);
              try {
                const res = await fetch(`${api}/api/meesa/sync`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ site }) });
                const result = await res.json();
                if (result.success) { alert(`Synced ${result.weeks} weeks from Andes!`); window.location.reload(); }
                else alert('Sync failed: ' + (result.error || 'Unknown error'));
              } catch (e) { alert('Sync error: ' + e.message); }
              setLoading(false);
            }} disabled={loading}>
              {loading ? 'Syncing...' : '🔄 Sync from Andes'}
            </button>
          </div>
        </div>
        <p style={{ fontSize: '0.8rem', color: '#666' }}>
          {filteredData.length} of {weeklyData.length} weeks shown
        </p>
      </div>

      {loading && <div className="card" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 40 }}><img src={loadingImage()} alt="Loading" style={{ width: 80, height: 80, animation: "spin 1.5s linear infinite" }} /><p style={{ marginTop: 12, fontSize: "0.85rem", color: "#666" }}>Loading weekly PPR data...</p><style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style></div>}

      {!loading && filteredData.length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <div style={{ marginBottom: 8, textAlign: 'right' }}>
            <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 12px' }} onClick={() => {
              const lines = PPR_LINES.filter(l => !essentialsOnly || !hiddenLineIds.includes(l.id));
              const weeks = filteredData.map(w => w.weekStart);
              const forecastHeaders = [...Array(forecastWeeks)].map((_, i) => `F${i + 1}`);
              let csv = 'Line Item,Type,YTD,' + weeks.join(',') + ',' + forecastHeaders.join(',') + '\n';
              lines.forEach(line => {
                // Rate row
                const ytd = ytdByLine[line.id] || {};
                const rateLabel = line.type === 'uph' ? 'UPH' : 'CPLH';
                const rates = filteredData.map(w => {
                  if (line.type === 'calc') {
                    const c = (w.cartons?.ibCartons || 0) + (w.cartons?.daCartons || 0);
                    const h = (w.lines?.totalInbound?.hours || 0) + (w.lines?.daBldgTotal?.hours || 0);
                    return h > 0 ? (c / h).toFixed(1) : '';
                  }
                  if (line.type === 'cplh' && line.cartonSource) {
                    let c = 0;
                    if (line.cartonSource === '_pidPlusAros') {
                      c = (w.cartons?.pid || 0) + (w.cartons?.aros || 0);
                    } else if (line.cartonSource === '_pallets') {
                      c = (w.cartons?.pr || 0) / 28.2; // CPP density
                    } else {
                      c = w.cartons?.[line.cartonSource] || 0;
                    }
                    const h = w.lines?.[line.id]?.hours || 0;
                    return h > 0 ? (c / h).toFixed(1) : '';
                  }
                  return w.lines?.[line.id]?.rate ? w.lines[line.id].rate.toFixed(1) : '';
                });
                const forecast = forecastByLine[line.id]?.forecast || [];
                csv += `${line.name},${rateLabel},${ytd.rate ? ytd.rate.toFixed(1) : ''},${rates.join(',')},${forecast.map(f => f ? f.toFixed(1) : '').join(',')}\n`;
                // Hours row
                const hours = filteredData.map(w => {
                  if (line.type === 'calc') return Math.round((w.lines?.totalInbound?.hours || 0) + (w.lines?.daBldgTotal?.hours || 0));
                  return w.lines?.[line.id]?.hours ? Math.round(w.lines[line.id].hours) : '';
                });
                const fcastHours = forecastByLine[line.id]?.buHours;
                const fcastHoursStr = [...Array(forecastWeeks)].map((_, i) => {
                  // Bottom-up lines have precomputed hours
                  if (fcastHours) return fcastHours[i] ? Math.round(fcastHours[i]) : '';
                  // Fallback: derive hours from forecast volume / forecast rate (matches on-screen table)
                  const w = effectiveInputs[i];
                  if (!w) return '';
                  const prPct = 100 - w.pidPct - w.arosPct;
                  const pid = Math.round(w.totalCartons * w.pidPct / 100);
                  const aros = Math.round(w.totalCartons * w.arosPct / 100);
                  const pr = Math.round(w.totalCartons * prPct / 100);
                  const eol = Math.round(pid * w.eolPct / 100);
                  const vols = { ibCartons: w.totalCartons, obCartons: w.totalCartons, daCartons: w.totalCartons, totalCartons: w.totalCartons * 2, pid, aros, pr, eol, fluidDirect: pid - eol, fluidOffline: eol + aros, _pidPlusAros: pid + aros, _pallets: pr / (w.cpp || 28.2), each: Math.round(w.totalCartons * (w.eachPct || 0.01) / 100), caseVol: Math.round(w.totalCartons * (w.casePct || 0.01) / 100), prep: Math.round(w.totalCartons * (w.prepPct || 0.01) / 100) };
                  const volKey = line.type === 'calc' ? 'totalCartons' : (line.cartonSource || 'totalCartons');
                  const vol = vols[volKey] || 0;
                  const rate = forecastByLine[line.id]?.forecast?.[i] || 0;
                  const hrs = rate > 0 ? vol / rate : 0;
                  return hrs ? Math.round(hrs) : '';
                }).join(',');
                csv += `${line.name},Hours,${ytd.hours ? Math.round(ytd.hours) : ''},${hours.join(',')},${fcastHoursStr}\n`;
              });
              const blob = new Blob([csv], { type: 'text/csv' });
              const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `meesa_${site}.csv`; a.click();
            }}>📥 Export CSV</button>
          </div>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, textAlign: 'left', minWidth: 160, position: 'sticky', left: 0, zIndex: 2 }}>Week Number</th>
                <th style={headerStyle}>YTD</th>
                {filteredData.map((week, i) => (
                  <th key={i} style={headerStyle}>{(() => { const d = new Date(week.weekStart + 'T12:00:00'); const jan1 = new Date(d.getFullYear(), 0, 1); return Math.ceil((((d - jan1) / 86400000) + jan1.getDay() + 1) / 7); })()}</th>
                ))}
                {[...Array(forecastWeeks)].map((_, i) => (
                  <th key={`f${i}`} style={{ ...headerStyle, background: '#1565c0' }}>F{i + 1}</th>
                ))}
              </tr>
              <tr>
                <th style={{ ...headerStyle, textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, fontSize: '0.6rem' }}>Date</th>
                <th style={{ ...headerStyle, fontSize: '0.6rem' }}></th>
                {filteredData.map((week, i) => (
                  <th key={i} style={{ ...headerStyle, fontSize: '0.6rem' }}>{week.weekStart || ''}</th>
                ))}
                {[...Array(forecastWeeks)].map((_, i) => (
                  <th key={`f${i}`} style={{ ...headerStyle, fontSize: '0.6rem', background: '#1565c0' }}>+{i + 1}w</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PPR_LINES.filter(line => !essentialsOnly || !hiddenLineIds.includes(line.id)).map(line => {
                const ytd = ytdByLine[line.id] || {};

                // Calculate rate for a given week based on line type
                const getWeekRate = (week) => {
                  if (line.type === 'calc') {
                    const cartons = (week.cartons?.ibCartons || 0) + (week.cartons?.daCartons || 0);
                    const hours = (week.lines?.totalInbound?.hours || 0) + (week.lines?.daBldgTotal?.hours || 0);
                    return hours > 0 ? cartons / hours : 0;
                  }
                  if (line.type === 'cplh' && line.cartonSource) {
                    let cartons = 0;
                    if (line.cartonSource === '_pidPlusAros') {
                      cartons = (week.cartons?.pid || 0) + (week.cartons?.aros || 0);
                    } else if (line.cartonSource === '_pallets') {
                      cartons = (week.cartons?.pr || 0) / 28.2; // CPP density
                    } else {
                      cartons = week.cartons?.[line.cartonSource] || 0;
                    }
                    const hours = week.lines?.[line.id]?.hours || 0;
                    return hours > 0 ? cartons / hours : 0;
                  }
                  // UPH - use stored PPR rate
                  return week.lines?.[line.id]?.rate || 0;
                };

                const getWeekHours = (week) => {
                  if (line.type === 'calc') {
                    return (week.lines?.totalInbound?.hours || 0) + (week.lines?.daBldgTotal?.hours || 0);
                  }
                  return week.lines?.[line.id]?.hours || 0;
                };

                const rateLabel = line.type === 'uph' ? 'UPH' : 'CPLH';

                return (
                  <React.Fragment key={line.id}>
                    {/* Rate row */}
                    <tr>
                      <td style={labelStyle}>
                        {line.id === 'transferOut' && (
                          <button onClick={() => setToExpanded(e => !e)}
                            title={toExpanded ? 'Collapse Transfer Out sub-lines' : 'Expand Transfer Out sub-lines'}
                            style={{ marginRight: 6, width: 16, height: 16, lineHeight: '14px', textAlign: 'center', padding: 0, border: '1px solid #999', borderRadius: 3, background: '#eceff1', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700 }}>
                            {toExpanded ? '−' : '+'}
                          </button>
                        )}
                        {line.name} <span style={{ fontSize: '0.55rem', color: '#888' }}>{rateLabel}</span></td>
                      <td style={{ ...cellStyle, fontWeight: 700 }}>{ytd.rate ? ytd.rate.toFixed(1) : '—'}</td>
                      {filteredData.map((week, i) => {
                        const rate = getWeekRate(week);
                        const d = week.lines?.[line.id];
                        const planRate = d?.planRate || 0;
                        const pct = planRate > 0 ? (rate / planRate) * 100 : 0;
                        return <td key={i} style={{ ...cellStyle, background: getHeatColor(pct) }}>{rate ? rate.toFixed(1) : '—'}</td>;
                      })}
                      {forecastByLine[line.id]?.forecast.map((f, i) => (
                        <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#1565c0', fontStyle: 'italic' }}>{f ? f.toFixed(1) : '—'}</td>
                      ))}
                    </tr>
                    {/* % to Plan row */}
                    <tr>
                      <td style={subLabelStyle}>% to Plan</td>
                      <td style={{ ...cellStyle, background: getHeatColor(ytd.pctToPlan), fontWeight: 600 }}>
                        {ytd.pctToPlan ? ytd.pctToPlan.toFixed(0) + '%' : '—'}
                      </td>
                      {filteredData.map((week, i) => {
                        const rate = getWeekRate(week);
                        const target = getPlanTarget(week, line);
                        const pct = target > 0 ? (rate / target) * 100 : 0;
                        return <td key={i} style={{ ...cellStyle, background: getHeatColor(pct) }}>{pct ? pct.toFixed(0) + '%' : '—'}</td>;
                      })}
                      {forecastByLine[line.id]?.forecast.map((f, i) => {
                        const planRate = ytdByLine[line.id]?.planRate || 0;
                        const pct = planRate > 0 ? (f / planRate) * 100 : 0;
                        return <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#1565c0', fontStyle: 'italic' }}>{pct ? pct.toFixed(0) + '%' : '—'}</td>;
                      })}
                    </tr>
                    {/* Hours row */}
                    <tr>
                      <td style={subLabelStyle}>Hours</td>
                      <td style={{ ...cellStyle, fontSize: '0.6rem', color: '#555' }}>
                        {ytd.hours ? Math.round(ytd.hours).toLocaleString() : '—'}
                      </td>
                      {filteredData.map((week, i) => {
                        const hours = getWeekHours(week);
                        return <td key={i} style={{ ...cellStyle, fontSize: '0.6rem', color: '#555' }}>{hours ? Math.round(hours).toLocaleString() : '—'}</td>;
                      })}
                      {effectiveInputs.map((w, i) => {
                        // Use bottom-up hours if available for this line
                        if (forecastByLine[line.id]?.buHours) {
                          const hours = forecastByLine[line.id].buHours[i] || 0;
                          // Admin/HR/IT gets an invisible editable input
                          if (line.id === 'adminHrIt') {
                            return <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#1565c0', fontStyle: 'italic', fontSize: '0.6rem', padding: '1px' }}>
                              <input type="text" value={adminHoursOverrides[i] !== undefined ? adminHoursOverrides[i] : (hours ? Math.round(hours) : '')}
                                onChange={e => setAdminHoursOverrides(prev => ({ ...prev, [i]: e.target.value }))}
                                style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center', fontSize: '0.6rem', color: '#1565c0', fontStyle: 'italic' }} />
                            </td>;
                          }
                          return <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#1565c0', fontStyle: 'italic', fontSize: '0.6rem' }}>{hours ? Math.round(hours).toLocaleString() : '—'}</td>;
                        }
                        const prPct = 100 - w.pidPct - w.arosPct;
                        const pid = Math.round(w.totalCartons * w.pidPct / 100);
                        const aros = Math.round(w.totalCartons * w.arosPct / 100);
                        const pr = Math.round(w.totalCartons * prPct / 100);
                        const eol = Math.round(pid * w.eolPct / 100);
                        const vols = { ibCartons: w.totalCartons, obCartons: w.totalCartons, daCartons: w.totalCartons, totalCartons: w.totalCartons * 2, pid, aros, pr, eol, fluidDirect: pid - eol, fluidOffline: eol + aros, _pidPlusAros: pid + aros, _pallets: pr / (w.cpp || 28.2), each: Math.round(w.totalCartons * (w.eachPct || 0.01) / 100), caseVol: Math.round(w.totalCartons * (w.casePct || 0.01) / 100), prep: Math.round(w.totalCartons * (w.prepPct || 0.01) / 100) };
                        const volKey = line.type === 'calc' ? 'totalCartons' : (line.cartonSource || 'totalCartons');
                        const vol = vols[volKey] || 0;
                        const rate = forecastByLine[line.id]?.forecast?.[i] || 0;
                        const hours = rate > 0 ? vol / rate : 0;
                        return <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#1565c0', fontStyle: 'italic', fontSize: '0.6rem' }}>{hours ? Math.round(hours).toLocaleString() : '—'}</td>;
                      })}
                    </tr>
                    {/* Hours Saved row */}
                    <tr style={{ borderBottom: '2px solid #000' }}>
                      <td style={subLabelStyle}>Hours Gained/Lost</td>
                      <td style={{ ...cellStyle, background: getHoursColor(ytd.hoursSaved), fontWeight: 600 }}>
                        {ytd.hoursSaved ? Math.round(ytd.hoursSaved).toLocaleString() : '—'}
                      </td>
                      {filteredData.map((week, i) => {
                        const rate = getWeekRate(week);
                        const hours = getWeekHours(week);
                        const target = getPlanTarget(week, line);
                        // Hours saved = (cartons / target rate) - actual hours
                        // Or equivalently: hours * (rate/target - 1) when rate > 0
                        let hrsSaved = 0;
                        if (target > 0 && rate > 0 && hours > 0) {
                          const cartons = rate * hours;
                          const plannedHours = cartons / target;
                          hrsSaved = plannedHours - hours;
                        } else if (planMode === 'op') {
                          hrsSaved = week.lines?.[line.id]?.hoursSaved || 0;
                        }
                        return <td key={i} style={{ ...cellStyle, background: getHoursColor(hrsSaved) }}>{hrsSaved ? Math.round(hrsSaved).toLocaleString() : '—'}</td>;
                      })}
                      {[...Array(forecastWeeks)].map((_, i) => (
                        <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#999', fontStyle: 'italic' }}>—</td>
                      ))}
                    </tr>
                    {/* Expanded Transfer Out sub-lines (derived from the week's
                        transferOut functions; display-only, historical data untouched).
                        Rate = DA cartons / summed bucket hours — same basis as the
                        parent Transfer Out line, so these reconcile to it. */}
                    {line.id === 'transferOut' && toExpanded && (() => {
                      const TO_BUCKETS = [
                        ['Transfer Out Support', (n) => !['Fluid Load - Case','Wall Builder','Robotics Operator','Palletize - Case'].includes(n)],
                        ['TO Fluid Load', (n) => n === 'Fluid Load - Case' || n === 'Wall Builder'],
                        ['TO RWC', (n) => n === 'Robotics Operator'],
                        ['TO Palletize', (n) => n === 'Palletize - Case'],
                      ];
                      const bucketHours = (week, pred) => (week.functions?.transferOut || []).reduce((s, f) => s + (pred(f.name) ? (f.hours || 0) : 0), 0);
                      return TO_BUCKETS.map(([bname, pred]) => {
                        // Only render buckets that actually have hours in the visible range
                        const anyHours = filteredData.some(w => bucketHours(w, pred) > 0);
                        if (!anyHours) return null;
                        // YTD rate across all weekly data for this bucket
                        let ytdCartons = 0, ytdHours = 0;
                        weeklyData.forEach(w => { const h = bucketHours(w, pred); if (h > 0) { ytdHours += h; ytdCartons += (w.cartons?.daCartons || 0); } });
                        const ytdRate = ytdHours > 0 ? ytdCartons / ytdHours : 0;
                        return (
                          <tr key={`to-sub-${bname}`} style={{ background: '#f7faf7' }}>
                            <td style={{ ...subLabelStyle, color: '#1b5e20', fontWeight: 600 }}>↳ {bname} <span style={{ fontSize: '0.55rem', color: '#888' }}>CPLH</span></td>
                            <td style={{ ...cellStyle, fontWeight: 600 }}>{ytdRate ? ytdRate.toFixed(1) : '—'}</td>
                            {filteredData.map((week, i) => {
                              const h = bucketHours(week, pred);
                              const c = week.cartons?.daCartons || 0;
                              const r = h > 0 ? c / h : 0;
                              return <td key={i} style={{ ...cellStyle, background: '#f1f8e9' }} title={h ? `${Math.round(h)} hrs` : ''}>{r ? r.toFixed(1) : '—'}</td>;
                            })}
                            {[...Array(forecastWeeks)].map((_, i) => (
                              <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#999' }}>—</td>
                            ))}
                          </tr>
                        );
                      });
                    })()}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Volume by Path table */}
      {!loading && filteredData.length > 0 && filteredData.some(w => w.cartons) && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <h3 className="section-title">Volume by Path (Weekly Cartons)</h3>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, textAlign: 'left', minWidth: 120, position: 'sticky', left: 0, zIndex: 2 }}>Carton Type</th>
                {filteredData.map((week, i) => (
                  <th key={i} style={headerStyle}>{(() => { const d = new Date(week.weekStart + 'T12:00:00'); const jan1 = new Date(d.getFullYear(), 0, 1); return Math.ceil((((d - jan1) / 86400000) + jan1.getDay() + 1) / 7); })()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {['pid', 'aros', 'pr', 'fluidDirect', 'fluidOffline', 'eol', 'pit', 'inbound', 'outbound'].map(key => {
                const labels = { pid: 'PID', aros: 'AROS', pr: 'PR / Pallet', fluidDirect: 'Fluid Direct', fluidOffline: 'Fluid Offline', eol: 'EOL', pit: 'PIT', inbound: 'Total Inbound', outbound: 'Total Outbound' };
                return (
                  <tr key={key} style={{ borderBottom: key === 'pit' || key === 'outbound' ? '2px solid #000' : '1px solid #ddd' }}>
                    <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>{labels[key]}</td>
                    {filteredData.map((week, i) => {
                      const val = week.cartons?.[key] || 0;
                      return <td key={i} style={cellStyle}>{val ? Math.round(val).toLocaleString() : '—'}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Volume Forecast */}
      {!loading && filteredData.length > 0 && (
        <VolumeForecast api={api} site={site} forecastByLine={forecastByLine} filteredData={filteredData} weekInputs={effectiveInputs} setWeekInputs={setWeekInputs} />
      )}

      {/* Function-level breakdown tables */}
      {!loading && filteredData.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, marginTop: 16 }}>
          <button className="btn btn-primary" onClick={saveOverrides} style={{ fontSize: '0.75rem', padding: '5px 14px' }}>
            {overridesSaved ? '✅ Saved!' : '💾 Save Overrides'}
          </button>
          <button className="btn btn-secondary" onClick={resetOverrides} style={{ fontSize: '0.75rem', padding: '5px 14px' }}>
            🔄 Reset to B3of4
          </button>
          <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '5px 14px' }} onClick={() => {
            const PARENTS = [
              { key: 'receiveDock', name: 'Receive Dock' },
              { key: 'receiveSupport', name: 'Receive Support' },
              { key: 'transferOut', name: 'Transfer Out' },
              { key: 'transferOutDock', name: 'Transfer Out Dock' },
            ];
            const weeks = filteredData.map(w => w.weekStart);
            const forecastHeaders = [...Array(forecastWeeks)].map((_, i) => `F${i + 1}`);
            let csv = 'Parent Line,Function,Casual Name,Metric,' + weeks.join(',') + ',' + forecastHeaders.join(',') + '\n';

            PARENTS.forEach(({ key: parentKey, name: parentName }) => {
              // Unique functions across all weeks for this parent
              const allFunctions = [];
              const seen = new Set();
              filteredData.forEach(week => {
                (week.functions?.[parentKey] || []).forEach(fn => {
                  if (!seen.has(fn.name)) { seen.add(fn.name); allFunctions.push(fn.name); }
                });
              });
              if (allFunctions.length === 0) return;

              const fnCartonMap = {
                'Vendor Line Load': 'pid', 'PID Manual Divert': 'pid', 'ReceiveParcel Unload': 'pid', 'Receive Dock Crew': 'pid',
                'ReceiveLine Unloader': 'aros', 'PID Sort Line': 'aros',
                'MHE Support': parentKey === 'transferOut' ? 'aros' : 'pid', 'Robotic Problem Solv': 'aros', 'Jackpot Kickout': 'aros', 'Robotic Water Spider': 'aros',
                'Vendor Sort': 'pr', 'Offline_Mods': parentKey === 'receiveSupport' ? 'pr' : 'eol',
                'IB Robotics Operator': 'pr', 'Rcv WaterSpider': 'pr', 'Case Rcv WaterSpider': 'pr',
                'PID Truck Unload': 'pr',
                'Tote Replenishment': 'eol', 'Transfer Out Kaizen': 'eol', 'Water Spider': 'eol',
                'Transfer Out Packing': 'fluidOffline', 'Wall Builder': 'fluidDirect', 'Robotics Operator': 'fluidOffline',
                'Fluid Load - Case': 'fluidOffline',
                'TransferOut DockCrew': 'daCartons', 'Dock Pallet Loader': 'pr', 'IXD Pallet Loader': 'pr',
                'Receive Dock TDR': 'pid', 'Inbound Dock Clerk': 'pid', 'PID Box Opener': 'pid',
              };
              const casualNames = {
                'Vendor Line Load': 'PID Unload', 'PID Manual Divert': 'PID Divert',
                'ReceiveParcel Unload': 'PID Reinject', 'Receive Dock Crew': 'PID Operator',
                'ReceiveLine Unloader': 'AROS Unload', 'PID Sort Line': 'AROS CSX Label',
                'Vendor Sort': 'PR Unload', 'IB Robotics Operator': 'Wrap/Lantech',
                'PID Truck Unload': 'PIT Unloader', 'Receive Dock TDR': 'IB TDR',
                'Inbound Dock Clerk': 'IB Clerk',
                'Offline_Mods': parentKey === 'receiveSupport' ? 'Pallet Build' : 'EOL Sort',
                'Rcv WaterSpider': 'PR Waterspider', 'Case Rcv WaterSpider': 'IB Pallet EPJ WS',
                'MHE Support': parentKey === 'receiveSupport' ? 'Jam Clear' : 'AROS Avery',
                'Wall Builder': 'Sorter Wallbuild', 'Robotics Operator': 'Offline Wallbuild',
                'Transfer Out Packing': 'Fluid WS', 'Jackpot Kickout': 'AROS Sort',
                'Robotic Problem Solv': 'AROS Divert', 'Robotic Water Spider': 'AROS Waterspider',
                'Transfer Out Kaizen': 'EOL Divert', 'Water Spider': 'EOL Waterspider',
                'TransferOut DockCrew': 'OB TDR', 'Dock Pallet Loader': 'Pallet Loader',
                'IXD Pallet Loader': 'OB EPJ Operator', 'Tote Replenishment': 'EOL Avery',
                'Transfer Out Ambssdr': 'Ambassador', 'TransferOut Training': 'Training',
              };
              const getCalcRate = (week, fnName) => {
                const fn = (week.functions?.[parentKey] || []).find(f => f.name === fnName);
                if (!fn || !fn.hours) return 0;
                const cartonKey = fnCartonMap[fnName];
                if (!cartonKey || !week.cartons) return fn.rate || 0;
                const cartons = week.cartons[cartonKey] || 0;
                return cartons > 0 ? cartons / fn.hours : 0;
              };

              // Group Transfer Out functions into the new PPR sub-line buckets
              // (display/export only, by function name) so the CSV matches the
              // on-screen table. Other parents export as one flat group.
              const EXP_TO_BUCKET_OF = {
                'Fluid Load - Case': 'TO Fluid Load', 'Wall Builder': 'TO Fluid Load',
                'Robotics Operator': 'TO RWC', 'Palletize - Case': 'TO Palletize',
              };
              const EXP_TO_ORDER = ['Transfer Out Support', 'TO Fluid Load', 'TO RWC', 'TO Palletize'];
              const expGroups = (() => {
                if (parentKey !== 'transferOut') return [[null, allFunctions]];
                const bb = {};
                allFunctions.forEach(fn => { const b = EXP_TO_BUCKET_OF[fn] || 'Transfer Out Support'; (bb[b] = bb[b] || []).push(fn); });
                return EXP_TO_ORDER.filter(b => bb[b]?.length).map(b => [b, bb[b]]);
              })();

              expGroups.forEach(([bucketName, bucketFns]) => {
                if (bucketName) csv += `${parentName},"${bucketName}",,,${filteredData.map(() => '').join(',')}${[...Array(forecastWeeks)].map(() => '').join(',')}\n`;
                bucketFns.forEach(fnName => {
                // Forecast rates (top 3 of 4 baseline + ramp, honoring rate overrides)
                const completeWeeks = weeklyData.length > 1 ? filteredData.slice(-4) : [];
                const histRates = completeWeeks.map(w => getCalcRate(w, fnName)).filter(r => r > 0);
                histRates.sort((a, b) => b - a);
                const top3 = histRates.slice(0, 3);
                const baseline = top3.length > 0 ? top3.reduce((s, v) => s + v, 0) / top3.length : 0;
                const fnRamp = rampFor(fnName);
                const fcastRates = [...Array(forecastWeeks)].map((_, i) => {
                  const ov = funcRateOverrides[fnName]?.[i];
                  if (ov !== undefined && ov !== null && ov !== '') return parseFloat(ov);
                  return Math.round(baseline * (1 + (fnRamp / 100) * (i + 1)) * 10) / 10;
                });

                // Rate row
                const actualRates = filteredData.map(w => { const r = getCalcRate(w, fnName); return r ? r.toFixed(1) : ''; });
                const casual = casualNames[fnName] || '';
                csv += `${parentName},"${fnName}","${casual}",Rate,${actualRates.join(',')},${fcastRates.map(r => r ? r.toFixed(1) : '').join(',')}\n`;

                // Hours row
                const actualHours = filteredData.map(w => {
                  const fn = (w.functions?.[parentKey] || []).find(f => f.name === fnName);
                  return fn?.hours ? Math.round(fn.hours) : '';
                });
                const fcastHours = [...Array(forecastWeeks)].map((_, i) => {
                  const w = effectiveInputs[i];
                  if (!w) return '';
                  const prPct = 100 - w.pidPct - w.arosPct;
                  const pid = Math.round(w.totalCartons * w.pidPct / 100);
                  const aros = Math.round(w.totalCartons * w.arosPct / 100);
                  const pr = Math.round(w.totalCartons * prPct / 100);
                  const eolV = Math.round(pid * w.eolPct / 100);
                  const vols = { ibCartons: w.totalCartons, obCartons: w.totalCartons, daCartons: w.totalCartons, totalCartons: w.totalCartons * 2, pid, aros, pr, eol: eolV, fluidDirect: pid - eolV, fluidOffline: eolV + aros, each: Math.round(w.totalCartons * (w.eachPct || 0.01) / 100), caseVol: Math.round(w.totalCartons * (w.casePct || 0.01) / 100), prep: Math.round(w.totalCartons * (w.prepPct || 0.01) / 100) };
                  const cartonKey = fnCartonMap[fnName];
                  const vol = cartonKey ? (vols[cartonKey] || 0) : 0;
                  const hoursOverride = funcHoursOverrides[fnName]?.[i];
                  const rate = fcastRates[i] || 0;
                  const hrs = hoursOverride !== undefined && hoursOverride !== '' ? parseFloat(hoursOverride) : (rate > 0 ? vol / rate : 0);
                  return hrs ? Math.round(hrs) : '';
                });
                csv += `${parentName},"${fnName}","${casual}",Hours,${actualHours.join(',')},${fcastHours.join(',')}\n`;
                });
                if (bucketName) {
                  const subActual = filteredData.map(w => {
                    const fns = w.functions?.[parentKey] || [];
                    const t = bucketFns.reduce((s, fn) => s + ((fns.find(f => f.name === fn)?.hours) || 0), 0);
                    return t ? Math.round(t) : '';
                  });
                  const subFcast = [...Array(forecastWeeks)].map((_, i) => {
                    const t = bucketFns.reduce((s, fn) => s + ((functionDetail[parentKey]?.forecastHours[fn]?.[i]) || 0), 0);
                    return t ? Math.round(t) : '';
                  });
                  csv += `${parentName},"${bucketName} — Total","",Hours,${subActual.join(',')},${subFcast.join(',')}\n`;
                }
              });
            });

            const blob = new Blob([csv], { type: 'text/csv' });
            const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `meesa_functions_${site}.csv`; a.click();
          }}>📥 Export Function CSV</button>
        </div>
      )}
      {!loading && filteredData.length > 0 && ['receiveDock', 'receiveSupport', 'transferOut', 'transferOutDock'].map(parentKey => {
        const parentNames = { receiveDock: 'Receive Dock', receiveSupport: 'Receive Support', transferOut: 'Transfer Out', transferOutDock: 'Transfer Out Dock' };
        // Function-level data comes from the shared `functionDetail` — the exact
        // same source the top-table bottom-up line hours are summed from, so the
        // detail rows always reconcile with the line total above.
        const fd = functionDetail[parentKey] || { functions: [], forecastRates: {}, forecastHours: {}, getCalcRate: () => 0, cmap: {} };
        const allFunctions = fd.functions;
        if (allFunctions.length === 0) return null;

        const fnCartonMap = fd.cmap;
        const getCalcRate = fd.getCalcRate;

        // Casual name mapping
        const casualNames = {
          'Vendor Line Load': 'PID Unload', 'PID Manual Divert': 'PID Divert',
          'ReceiveParcel Unload': 'PID Reinject', 'Receive Dock Crew': 'PID Operator',
          'ReceiveLine Unloader': 'AROS Unload', 'PID Sort Line': 'AROS CSX Label',
          'Vendor Sort': 'PR Unload', 'IB Robotics Operator': 'Wrap/Lantech',
          'PID Truck Unload': 'PIT Unloader', 'Receive Dock TDR': 'IB TDR',
          'Inbound Dock Clerk': 'IB Clerk',
          'Offline_Mods': parentKey === 'receiveSupport' ? 'Pallet Build' : 'EOL Sort',
          'Rcv WaterSpider': 'PR Waterspider', 'Case Rcv WaterSpider': 'IB Pallet EPJ WS',
          'MHE Support': parentKey === 'receiveSupport' ? 'Jam Clear' : 'AROS Avery',
          'Wall Builder': 'Sorter Wallbuild', 'Robotics Operator': 'Offline Wallbuild',
          'Transfer Out Packing': 'Fluid WS', 'Jackpot Kickout': 'AROS Sort',
          'Robotic Problem Solv': 'AROS Divert', 'Robotic Water Spider': 'AROS Waterspider',
          'Transfer Out Kaizen': 'EOL Divert', 'Water Spider': 'EOL Waterspider',
          'TransferOut DockCrew': 'OB TDR', 'Dock Pallet Loader': 'Pallet Loader',
          'IXD Pallet Loader': 'OB EPJ Operator', 'Tote Replenishment': 'EOL Avery',
          'Transfer Out Ambssdr': 'Ambassador', 'TransferOut Training': 'Training',
        };

        const funcForecasts = fd.forecastRates;

        // Split the Transfer Out detail into the new PPR sub-line buckets by
        // FUNCTION NAME (display-only; historical hours are untouched). Works for
        // both old weeks (one 'transferOut' functions group) and new weeks — the
        // rows are regrouped the same way either way, so the three buckets still
        // collectively reconcile to the top-table Transfer Out line.
        const TO_BUCKET_OF = {
          'Fluid Load - Case': 'TO Fluid Load', 'Wall Builder': 'TO Fluid Load',
          'Robotics Operator': 'TO RWC',
          'Palletize - Case': 'TO Palletize',
          // everything else in the Transfer Out group -> Transfer Out Support
        };
        const TO_BUCKET_ORDER = ['Transfer Out Support', 'TO Fluid Load', 'TO RWC', 'TO Palletize'];
        // Build ordered [bucketName, [fnNames...]] groups. For non-transferOut
        // parents, a single unnamed group preserves the existing flat layout.
        const fnGroups = (() => {
          if (parentKey !== 'transferOut') return [[null, allFunctions]];
          const byBucket = {};
          allFunctions.forEach(fn => {
            const b = TO_BUCKET_OF[fn] || 'Transfer Out Support';
            (byBucket[b] = byBucket[b] || []).push(fn);
          });
          return TO_BUCKET_ORDER.filter(b => byBucket[b]?.length).map(b => [b, byBucket[b]]);
        })();

        return (
          <div key={parentKey} className="card" style={{ overflowX: 'auto' }}>
            <h3 className="section-title">{parentNames[parentKey]} — Function Detail</h3>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...headerStyle, textAlign: 'left', minWidth: 140, position: 'sticky', left: 0, zIndex: 2 }}>Function</th>
                  {filteredData.map((week, i) => (
                    <th key={i} style={headerStyle}>{(() => { const d = new Date(week.weekStart + 'T12:00:00'); const jan1 = new Date(d.getFullYear(), 0, 1); return Math.ceil((((d - jan1) / 86400000) + jan1.getDay() + 1) / 7); })()}</th>
                  ))}
                  {[...Array(forecastWeeks)].map((_, i) => (
                    <th key={`f${i}`} style={{ ...headerStyle, background: '#1565c0' }}>F{i + 1}</th>
                  ))}
                  <th style={{ ...headerStyle, background: '#f57f17', color: '#000', minWidth: 35 }}>Ramp%</th>
                  <th style={{ ...headerStyle, background: '#666', minWidth: 30 }}>Copy</th>
                </tr>
              </thead>
              <tbody>
                {fnGroups.map(([bucketName, bucketFns]) => (
                  <React.Fragment key={bucketName || '_flat'}>
                  {bucketName && (
                    <tr>
                      <td colSpan={1 + filteredData.length + forecastWeeks + 2}
                          style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, background: '#eceff1', color: '#1b5e20', position: 'sticky', left: 0, zIndex: 1 }}>
                        {bucketName}
                      </td>
                    </tr>
                  )}
                  {bucketFns.map(fnName => {
                  return (
                    <React.Fragment key={fnName}>
                      {/* Rate row */}
                      <tr>
                        <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>{fnName}{casualNames[fnName] ? <span style={{ fontWeight: 400, color: '#666' }}> ({casualNames[fnName]})</span> : ''}</td>
                        {filteredData.map((week, i) => {
                          const rate = getCalcRate(week, fnName);
                          return <td key={i} style={{ ...cellStyle, background: rate ? '#f5f5f5' : 'transparent' }}>{rate ? rate.toFixed(1) : '—'}</td>;
                        })}
                        {(funcForecasts[fnName] || []).map((f, i) => (
                          <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', padding: '1px' }}>
                            <input type="text" value={funcRateOverrides[fnName]?.[i] !== undefined ? funcRateOverrides[fnName][i] : (f ? f.toFixed(1) : '')}
                              onChange={e => {
                                const v = e.target.value;
                                setFuncRateOverrides(prev => ({ ...prev, [fnName]: { ...(prev[fnName] || {}), [i]: v } }));
                                // Back-calculate hours from rate and clear hours override
                                if (v === '') {
                                  setFuncHoursOverrides(prev => { const n = { ...prev }; if (n[fnName]) { delete n[fnName][i]; if (Object.keys(n[fnName]).length === 0) delete n[fnName]; } return n; });
                                } else {
                                  setFuncHoursOverrides(prev => { const n = { ...prev }; if (n[fnName]) { delete n[fnName][i]; if (Object.keys(n[fnName]).length === 0) delete n[fnName]; } return n; });
                                }
                              }}
                              style={{ width: '100%', border: 'none', textAlign: 'center', fontSize: '0.7rem', color: '#1565c0', fontStyle: 'italic', background: 'transparent', padding: '2px' }} />
                          </td>
                        ))}
                        <td style={{ ...cellStyle, background: '#fff9c4', padding: '1px' }}>
                          <input type="text" value={funcRampOverrides[fnName] !== undefined ? funcRampOverrides[fnName] : ''}
                            placeholder={`${rampPct}`}
                            inputMode="decimal" aria-label={`${fnName} ramp percent`}
                            onChange={e => { const v = e.target.value; if (!DECIMAL_RE.test(v)) return; setFuncRampOverrides(prev => ({ ...prev, [fnName]: v === '' ? undefined : v })); }}
                            style={{ width: 30, border: 'none', textAlign: 'center', fontSize: '0.6rem', background: 'transparent', padding: '1px' }} />
                        </td>
                        <td style={{ ...cellStyle, padding: '1px' }}>
                          <button title="Copy Rate F1 → All" onClick={() => {
                            const f1Rate = funcRateOverrides[fnName]?.[0] !== undefined ? funcRateOverrides[fnName][0] : ((funcForecasts[fnName] || [])[0] ? (funcForecasts[fnName] || [])[0].toFixed(1) : '');
                            if (f1Rate) {
                              const rateOvs = {};
                              for (let idx = 0; idx < forecastWeeks; idx++) rateOvs[idx] = String(f1Rate);
                              setFuncRateOverrides(prev => ({ ...prev, [fnName]: rateOvs }));
                              // Clear hours overrides so they recalculate from rate
                              setFuncHoursOverrides(prev => { const n = { ...prev }; delete n[fnName]; return n; });
                            }
                          }} style={{ fontSize: '0.45rem', padding: '1px 2px', border: '1px solid #ccc', borderRadius: 2, cursor: 'pointer', background: '#e3f2fd', display: 'block', margin: '1px auto' }}>R⟹</button>
                        </td>
                      </tr>
                      {/* Hours row */}
                      <tr style={{ borderBottom: '1px solid #ccc' }}>
                        <td style={{ ...cellStyle, textAlign: 'left', fontSize: '0.6rem', color: '#666', paddingLeft: 16, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>Hours</td>
                        {filteredData.map((week, i) => {
                          const fn = (week.functions?.[parentKey] || []).find(f => f.name === fnName);
                          const hours = fn?.hours || 0;
                          return <td key={i} style={{ ...cellStyle, fontSize: '0.6rem', color: '#666' }}>{hours ? hours.toFixed(0) : '—'}</td>;
                        })}
                        {effectiveInputs.map((w, i) => {
                          const prPct = 100 - w.pidPct - w.arosPct;
                          const pid = Math.round(w.totalCartons * w.pidPct / 100);
                          const aros = Math.round(w.totalCartons * w.arosPct / 100);
                          const pr = Math.round(w.totalCartons * prPct / 100);
                          const eolV = Math.round(pid * w.eolPct / 100);
                          const vols = { ibCartons: w.totalCartons, obCartons: w.totalCartons, daCartons: w.totalCartons, totalCartons: w.totalCartons * 2, pid, aros, pr, eol: eolV, fluidDirect: pid - eolV, fluidOffline: eolV + aros, each: Math.round(w.totalCartons * (w.eachPct || 0.01) / 100), caseVol: Math.round(w.totalCartons * (w.casePct || 0.01) / 100), prep: Math.round(w.totalCartons * (w.prepPct || 0.01) / 100) };
                          const cartonKey = fnCartonMap[fnName];
                          const vol = cartonKey ? (vols[cartonKey] || 0) : 0;
                          const hoursOverride = funcHoursOverrides[fnName]?.[i];
                          const rate = (funcForecasts[fnName] || [])[i] || 0;
                          const hrs = hoursOverride !== undefined && hoursOverride !== '' ? parseFloat(hoursOverride) : (rate > 0 ? vol / rate : 0);
                          return <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', padding: '1px' }}>
                            <input type="text" value={hoursOverride !== undefined ? hoursOverride : (hrs ? Math.round(hrs) : '')}
                              onChange={e => {
                                const val = e.target.value;
                                setFuncHoursOverrides(prev => ({ ...prev, [fnName]: { ...(prev[fnName] || {}), [i]: val } }));
                                // Back-calculate rate from hours
                                const h = parseFloat(val);
                                if (h > 0 && vol > 0) {
                                  setFuncRateOverrides(prev => ({ ...prev, [fnName]: { ...(prev[fnName] || {}), [i]: (vol / h).toFixed(1) } }));
                                } else if (val === '') {
                                  // Clear both overrides
                                  setFuncHoursOverrides(prev => { const n = { ...prev }; if (n[fnName]) { delete n[fnName][i]; if (Object.keys(n[fnName]).length === 0) delete n[fnName]; } return n; });
                                  setFuncRateOverrides(prev => { const n = { ...prev }; if (n[fnName]) { delete n[fnName][i]; if (Object.keys(n[fnName]).length === 0) delete n[fnName]; } return n; });
                                }
                              }}
                              style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center', fontSize: '0.6rem', color: '#1565c0', fontStyle: 'italic' }} />
                          </td>;
                        })}
                        <td style={cellStyle}></td>
                        <td style={{ ...cellStyle, padding: '1px' }}>
                          <button title="Copy Hours F1 → All" onClick={() => {
                            const w = effectiveInputs[0];
                            const prPct = 100 - w.pidPct - w.arosPct;
                            const pid = Math.round(w.totalCartons * w.pidPct / 100);
                            const aros = Math.round(w.totalCartons * w.arosPct / 100);
                            const pr = Math.round(w.totalCartons * prPct / 100);
                            const eolV = Math.round(pid * w.eolPct / 100);
                            const vols = { ibCartons: w.totalCartons, obCartons: w.totalCartons, daCartons: w.totalCartons, totalCartons: w.totalCartons * 2, pid, aros, pr, eol: eolV, fluidDirect: pid - eolV, fluidOffline: eolV + aros, each: Math.round(w.totalCartons * (w.eachPct || 0.01) / 100), caseVol: Math.round(w.totalCartons * (w.casePct || 0.01) / 100), prep: Math.round(w.totalCartons * (w.prepPct || 0.01) / 100) };
                            const cartonKey = fnCartonMap[fnName];
                            const vol = cartonKey ? (vols[cartonKey] || 0) : 0;
                            const rate = funcRateOverrides[fnName]?.[0] !== undefined ? parseFloat(funcRateOverrides[fnName][0]) : ((funcForecasts[fnName] || [])[0] || 0);
                            const f1Hours = rate > 0 ? Math.round(vol / rate) : 0;
                            if (f1Hours > 0) {
                              const hoursOvs = {};
                              const rateOvs = {};
                              for (let idx = 0; idx < forecastWeeks; idx++) {
                                hoursOvs[idx] = String(f1Hours);
                                const wi = effectiveInputs[idx];
                                const pp = 100 - wi.pidPct - wi.arosPct;
                                const p = Math.round(wi.totalCartons * wi.pidPct / 100);
                                const a = Math.round(wi.totalCartons * wi.arosPct / 100);
                                const r2 = Math.round(wi.totalCartons * pp / 100);
                                const e2 = Math.round(p * wi.eolPct / 100);
                                const v2 = { ibCartons: wi.totalCartons, obCartons: wi.totalCartons, daCartons: wi.totalCartons, totalCartons: wi.totalCartons * 2, pid: p, aros: a, pr: r2, eol: e2, fluidDirect: p - e2, fluidOffline: e2 + a, each: Math.round(wi.totalCartons * (wi.eachPct || 0.01) / 100), caseVol: Math.round(wi.totalCartons * (wi.casePct || 0.01) / 100), prep: Math.round(wi.totalCartons * (wi.prepPct || 0.01) / 100) };
                                const fVol = cartonKey ? (v2[cartonKey] || 0) : 0;
                                rateOvs[idx] = fVol > 0 ? (fVol / f1Hours).toFixed(1) : '';
                              }
                              setFuncHoursOverrides(prev => ({ ...prev, [fnName]: hoursOvs }));
                              setFuncRateOverrides(prev => ({ ...prev, [fnName]: rateOvs }));
                            }
                          }} style={{ fontSize: '0.45rem', padding: '1px 2px', border: '1px solid #ccc', borderRadius: 2, cursor: 'pointer', background: '#e8f5e9', display: 'block', margin: '1px auto' }}>H⟹</button>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                  })}
                  {bucketName && (
                    <tr style={{ borderTop: '2px solid #000', borderBottom: '2px solid #000', background: '#f1f8e9' }}>
                      <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, position: 'sticky', left: 0, background: '#f1f8e9', zIndex: 1 }}>{bucketName} — Total Hours</td>
                      {filteredData.map((week, i) => {
                        const fns = week.functions?.[parentKey] || [];
                        const tot = bucketFns.reduce((s, fn) => s + ((fns.find(f => f.name === fn)?.hours) || 0), 0);
                        return <td key={i} style={{ ...cellStyle, fontWeight: 700 }}>{tot ? tot.toFixed(0) : '—'}</td>;
                      })}
                      {[...Array(forecastWeeks)].map((_, i) => {
                        const tot = bucketFns.reduce((s, fn) => s + ((fd.forecastHours[fn]?.[i]) || 0), 0);
                        return <td key={`f${i}`} style={{ ...cellStyle, fontWeight: 700, background: '#e3f2fd' }}>{tot ? Math.round(tot) : '—'}</td>;
                      })}
                      <td style={cellStyle}></td>
                      <td style={cellStyle}></td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* RSR Support — Function Detail (single function: IDRT). IDRT labor can
          be tracked in THREE places, so hours = EOL Sort (Offline_Mods) +
          AROS Sort (Jackpot Kickout) functionRollup hours + the rsrSupport PPR
          line hours. Rate = (EOL cartons + AROS cartons) / those hours. This is
          historic reconstruction; forward weeks keep landing in the same three
          buckets, so the formula holds. Forecast = B3of4 of the IDRT rate + ramp. */}
      {!loading && filteredData.length > 0 && (() => {
        const idrtHours = (week) => {
          const fns = week.functions?.transferOut || [];
          const eolSort = fns.find(f => f.name === 'Offline_Mods')?.hours || 0;
          const arosSort = fns.find(f => f.name === 'Jackpot Kickout')?.hours || 0;
          const rsr = week.lines?.rsrSupport?.hours || 0;
          return eolSort + arosSort + rsr;
        };
        const idrtCartons = (week) => (week.cartons?.eol || 0) + (week.cartons?.aros || 0);
        const idrtRate = (week) => { const h = idrtHours(week); return h > 0 ? idrtCartons(week) / h : 0; };

        // Forecast: B3of4 of the historic IDRT rate, then + ramp (same convention
        // as the other MEESA bottom-up functions).
        const histRates = filteredData.map(idrtRate).filter(r => r > 0);
        const top3 = [...histRates].sort((a, b) => b - a).slice(0, 3);
        const baseRate = top3.length ? top3.reduce((s, v) => s + v, 0) / top3.length : 0;
        const fcRates = [...Array(forecastWeeks)].map((_, i) => baseRate * (1 + (rampPct / 100) * (i + 1)));

        return (
          <div className="card" style={{ overflowX: 'auto' }}>
            <h3 className="section-title">RSR Support — Function Detail</h3>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...headerStyle, textAlign: 'left', minWidth: 140, position: 'sticky', left: 0, zIndex: 2 }}>Function</th>
                  {filteredData.map((week, i) => (
                    <th key={i} style={headerStyle}>{(() => { const d = new Date(week.weekStart + 'T12:00:00'); const jan1 = new Date(d.getFullYear(), 0, 1); return Math.ceil((((d - jan1) / 86400000) + jan1.getDay() + 1) / 7); })()}</th>
                  ))}
                  {[...Array(forecastWeeks)].map((_, i) => <th key={`f${i}`} style={{ ...headerStyle, background: '#1565c0' }}>F{i + 1}</th>)}
                </tr>
              </thead>
              <tbody>
                {/* Rate row */}
                <tr>
                  <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>IDRT <span style={{ fontWeight: 400, color: '#666' }}>(EOL Sort + AROS Sort + RSR PPR)</span></td>
                  {filteredData.map((week, i) => { const r = idrtRate(week); return <td key={i} style={{ ...cellStyle, background: r ? '#f5f5f5' : 'transparent' }}>{r ? r.toFixed(1) : '—'}</td>; })}
                  {fcRates.map((r, i) => <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', fontStyle: 'italic', color: '#1565c0' }}>{r ? r.toFixed(1) : '—'}</td>)}
                </tr>
                {/* Hours row */}
                <tr>
                  <td style={{ ...cellStyle, textAlign: 'left', fontSize: '0.62rem', color: '#555', position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>Hours</td>
                  {filteredData.map((week, i) => { const h = idrtHours(week); return <td key={i} style={{ ...cellStyle, fontSize: '0.65rem', color: '#555' }}>{h ? h.toFixed(0) : '—'}</td>; })}
                  {fcRates.map((r, i) => { const vols = _volsForWeek(effectiveInputs[i]); const vol = (vols.eol || 0) + (vols.aros || 0); const h = r > 0 ? vol / r : 0; return <td key={`f${i}`} style={{ ...cellStyle, fontSize: '0.65rem', background: '#e3f2fd', color: '#1565c0' }}>{h ? Math.round(h) : '—'}</td>; })}
                </tr>
              </tbody>
            </table>
          </div>
        );
      })()}

      {!loading && weeklyData.length === 0 && (
        <div className="card">
          <p style={{ color: '#666' }}>No weekly data available. Data will populate as the FCLM sync captures weekly PPR summaries, or upload via CSV.</p>
          <div style={{ marginTop: 16 }}>
            <h3 className="section-title">Import Weekly Data</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const fileInput = e.target.querySelector('input[type="file"]');
              if (!fileInput.files[0]) return;
              const text = await fileInput.files[0].text();
              // Parse and upload
              try {
                const payload = { site, data: text };
                const res = await fetch(`${api}/api/meesa/upload`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });
                const result = await res.json();
                if (result.success) { alert('Data uploaded!'); window.location.reload(); }
                else alert('Upload failed: ' + (result.error || 'Unknown error'));
              } catch (err) { alert('Upload error: ' + err.message); }
            }} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="file" accept=".csv" />
              <button type="submit" className="btn btn-primary">Upload CSV</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

